import {
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { translateKey } from 'src/locales/translate';
import { WarehouseQuantityMeasure } from 'woofwoof-api';
import { formatProductStock } from '../../utils/stockDisplay';
import { DemandLineValues } from './WarehouseDemandRequestForm.schema';
import WarehouseDemandSourceBadge from './WarehouseDemandSourceBadge';

const f = (key: string): string => translateKey(`warehouse.demand.form.${key}`);
const packageShort = (): string =>
  translateKey('warehouse.common.packageShort');

interface WarehouseDemandLineRowProps {
  index: number;
  line: DemandLineValues;
  disabled: boolean;
  onRemove: () => void;
}

/**
 * One requested product in the two rows of a Sale line: its full name with
 * where it comes from (and, for a Warehouse Product, its stock), then the
 * optional quantity. A Warehouse Product is counted in op. or jedn., chosen
 * in the same field; a product outside the Warehouse only in op.
 */
export default function WarehouseDemandLineRow({
  index,
  line,
  disabled,
  onRemove,
}: WarehouseDemandLineRowProps): JSX.Element {
  const { product } = line;
  const stock = product?.stock;

  return (
    <Box
      data-testid="warehouse-demand-line"
      sx={{ py: 1.5, borderTop: 1, borderColor: 'divider' }}
    >
      <Stack direction="row" alignItems="flex-start" gap={1}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack alignItems="flex-start" spacing={0.5}>
            <Typography variant="subtitle2">
              {product ? product.name : line.requestedName}
            </Typography>
            <WarehouseDemandSourceBadge isCatalogueProduct={Boolean(product)} />
          </Stack>
          {stock && (
            <Typography
              variant="caption"
              color={stock.onHandMilli <= 0 ? 'warning.dark' : 'text.secondary'}
            >
              {translateKey('warehouse.sales.onHand', {
                stock: formatProductStock(stock.onHandMilli, stock),
              })}
            </Typography>
          )}
        </Box>
        <IconButton
          size="small"
          color="error"
          aria-label={f('removeLine')}
          onClick={onRemove}
          disabled={disabled}
          data-testid="warehouse-demand-line-remove"
        >
          <Iconify icon="eva:trash-2-outline" />
        </IconButton>
      </Stack>
      <RHFTextField
        name={`lines.${index}.quantity`}
        size="small"
        placeholder={f('quantityShort')}
        disabled={disabled}
        fullWidth={false}
        sx={{ mt: 1, width: 130 }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {product ? (
                <Controller
                  name={`lines.${index}.measure`}
                  render={({ field }) => (
                    <Select
                      {...field}
                      variant="standard"
                      disableUnderline
                      disabled={disabled}
                      inputProps={{ 'aria-label': f('measure') }}
                      data-testid="warehouse-demand-line-measure"
                    >
                      <MenuItem value={WarehouseQuantityMeasure.Package}>
                        {packageShort()}
                      </MenuItem>
                      <MenuItem value={WarehouseQuantityMeasure.Unit}>
                        {translateKey('warehouse.common.unitShort')}
                      </MenuItem>
                    </Select>
                  )}
                />
              ) : (
                packageShort()
              )}
            </InputAdornment>
          ),
        }}
        inputProps={{
          inputMode: 'decimal',
          'aria-label': f('quantityShort'),
          'data-testid': 'warehouse-demand-line-quantity',
        }}
      />
    </Box>
  );
}
